import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useMouseParallax } from '../hooks/useMouseParallax'
import { useReducedMotion } from '../hooks/useReducedMotion'

const stats = [
  { label: 'Focus', value: 72 },
  { label: 'Energia', value: 45 },
  { label: 'Disciplina', value: 58 },
  { label: 'Corpo', value: 38 },
  { label: 'Coraggio', value: 51 },
  { label: 'Stress Control', value: 61 },
]

function D20({ className, style }) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" />
      <line x1="50" y1="5" x2="50" y2="50" />
      <line x1="95" y1="25" x2="50" y2="50" />
      <line x1="95" y1="75" x2="50" y2="50" />
      <line x1="50" y1="95" x2="50" y2="50" />
      <line x1="5" y1="75" x2="50" y2="50" />
      <line x1="5" y1="25" x2="50" y2="50" />
      <circle cx="50" cy="52" r="16" fill="#050510" stroke="none" />
      <text x="50" y="58" textAnchor="middle" fill="#ffffff" stroke="none" fontSize="20" fontFamily="Orbitron" fontWeight="bold">20</text>
    </svg>
  )
}

function MangaPage({ className, style, children }) {
  return (
    <div className={`absolute ${className}`} style={style}>
      <div className="w-full h-full bg-nerd-card border border-nerd-purple/20 rounded-sm p-2 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-nerd-purple/30" />
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-nerd-purple/30" />
        <div className="w-full h-full bg-gradient-to-br from-nerd-purple/5 to-transparent flex items-center justify-center">
          {children}
        </div>
      </div>
    </div>
  )
}

export default function HeroSection() {
  const reduced = useReducedMotion()
  const mouse = useMouseParallax(reduced ? 0 : 25)
  const [loaded, setLoaded] = useState(false)
  const heroRef = useRef(null)

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100)
    return () => clearTimeout(t)
  }, [])

  return (
    <section ref={heroRef} className="relative w-full min-h-screen overflow-hidden flex items-center justify-center" style={{ background: '#050510' }}>
      {/* Deep background */}
      <motion.div className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(139,92,246,0.1) 0%, rgba(5,5,16,1) 65%)',
          x: reduced ? 0 : mouse.x * 0.2,
          y: reduced ? 0 : mouse.y * 0.2,
        }}
      />

      {/* Grid floor */}
      <div className="absolute bottom-0 left-0 right-0 h-[55vh] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(139,92,246,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.08) 1px, transparent 1px)',
          backgroundSize: '70px 70px',
          transform: 'perspective(500px) rotateX(65deg)',
          transformOrigin: 'center bottom',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
        }}
      />

      {/* Floating orbs */}
      {!reduced && (
        <>
          <motion.div className="absolute w-72 h-72 rounded-full blur-[120px] pointer-events-none"
            style={{ background: 'rgba(139,92,246,0.12)', top: '15%', left: '5%' }}
            animate={{ y: [0, -25, 0], x: [0, 12, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div className="absolute w-56 h-56 rounded-full blur-[100px] pointer-events-none"
            style={{ background: 'rgba(0,212,255,0.1)', bottom: '25%', right: '10%' }}
            animate={{ y: [0, 18, 0], x: [0, -8, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
          />
        </>
      )}

      {/* Portal */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] md:w-[600px] md:h-[600px] pointer-events-none"
        style={{
          x: reduced ? 0 : mouse.x * 0.5,
          y: reduced ? 0 : mouse.y * 0.5,
        }}
      >
        <div className="absolute inset-0 rounded-full portal-glow opacity-60" />
        <div className="absolute inset-4 rounded-full border border-nerd-purple/20" />
        <div className="absolute inset-8 rounded-full border border-nerd-blue/15" />
        <div className="absolute inset-12 rounded-full border border-nerd-purple/10" />
        {!reduced && (
          <motion.div className="absolute inset-0 rounded-full"
            style={{ border: '1px solid rgba(139,92,246,0.15)' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 60%)',
          }}
        >
          <div className="text-center">
            <div className="text-nerd-purple/20 text-[8px] font-display tracking-[0.3em] uppercase mb-2">Portale Attivo</div>
            <div className="w-2 h-2 rounded-full bg-nerd-blue/40 mx-auto animate-pulse" />
          </div>
        </div>
      </div>

      {/* Floating manga pages */}
      {!reduced && (
        <>
          <MangaPage className="w-20 h-28 top-[15%] left-[8%] opacity-40"
            style={{ animation: 'mangaFloat 12s ease-in-out infinite' }}
          >
            <div className="w-full h-1 bg-nerd-purple/20 mb-1" />
            <div className="w-3/4 h-1 bg-nerd-purple/10 mb-1" />
            <div className="w-full h-1 bg-nerd-purple/15 mb-1" />
            <div className="w-1/2 h-1 bg-nerd-purple/10" />
          </MangaPage>
          <MangaPage className="w-16 h-24 top-[60%] right-[6%] opacity-30"
            style={{ animation: 'mangaFloat 14s ease-in-out infinite 2s' }}
          >
            <div className="w-full h-1 bg-nerd-blue/20 mb-1" />
            <div className="w-2/3 h-1 bg-nerd-blue/10 mb-1" />
            <div className="w-full h-1 bg-nerd-blue/15" />
          </MangaPage>
          <MangaPage className="w-14 h-20 top-[75%] left-[15%] opacity-25"
            style={{ animation: 'mangaFloat 10s ease-in-out infinite 4s' }}
          >
            <div className="w-full h-1 bg-nerd-gold/20 mb-1" />
            <div className="w-3/4 h-1 bg-nerd-gold/10" />
          </MangaPage>
        </>
      )}

      {/* D20 dice */}
      {!reduced && (
        <>
          <motion.div className="absolute top-[20%] right-[18%] text-nerd-purple/30 w-12 h-12 md:w-16 md:h-16"
            animate={{ rotate: 360, y: [0, -10, 0] }}
            transition={{ rotate: { duration: 20, repeat: Infinity, ease: 'linear' }, y: { duration: 5, repeat: Infinity, ease: 'easeInOut' } }}
          >
            <D20 className="w-full h-full" />
          </motion.div>
          <motion.div className="absolute bottom-[25%] left-[12%] text-nerd-blue/25 w-8 h-8 md:w-10 md:h-10"
            animate={{ rotate: -360, y: [0, 8, 0] }}
            transition={{ rotate: { duration: 25, repeat: Infinity, ease: 'linear' }, y: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 } }}
          >
            <D20 className="w-full h-full" />
          </motion.div>
        </>
      )}

      {/* HUD Stats - Left */}
      <motion.div className="absolute left-3 xl:left-6 top-1/2 -translate-y-1/2 z-10 hidden xl:flex flex-col gap-2"
        initial={{ opacity: 0, x: -40 }}
        animate={loaded ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        {stats.map((stat, i) => (
          <motion.div key={stat.label} className="hud-stat w-36"
            initial={{ opacity: 0, x: -25 }}
            animate={loaded ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.6 + i * 0.12 }}
          >
            <div className="flex justify-between text-[9px] text-nerd-gray mb-1">
              <span>{stat.label}</span>
              <span className="text-nerd-cyan">{stat.value}%</span>
            </div>
            <div className="hud-stat-bar">
              <motion.div className="hud-stat-fill"
                initial={{ width: 0 }}
                animate={loaded ? { width: `${stat.value}%` } : {}}
                transition={{ duration: 1.2, delay: 0.9 + i * 0.12, ease: 'easeOut' }}
              />
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Central content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-nerd-purple to-transparent mx-auto mb-6"
          initial={{ scaleX: 0 }}
          animate={loaded ? { scaleX: 1 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
        />

        <motion.h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-5"
          initial={{ opacity: 0, y: 35, scale: 0.96 }}
          animate={loaded ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="block text-nerd-white">La tua vita non è finita.</span>
          <motion.span className="block text-gradient mt-2"
            initial={{ opacity: 0, y: 18 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.55 }}
          >
            Sei solo fermo al checkpoint.
          </motion.span>
        </motion.h1>

        <motion.p className="hero-emotional text-nerd-white/90 text-lg sm:text-xl md:text-2xl font-medium max-w-2xl mx-auto mb-3 leading-relaxed"
          initial={{ opacity: 0, y: 18 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.75 }}
        >
          Non devi smettere di amare i tuoi mondi.
          <br />
          Devi solo tornare ad agire nel tuo.
        </motion.p>

        <motion.p className="hero-description text-nerd-gray text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 18 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          Coaching mentale e fisico per nerd, anime lover, gamer e persone bloccate nella safe zone.
        </motion.p>

        <motion.div className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 18 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <a href="#lead-game" className="nerd-btn-primary text-sm">Libro-Game Mattutino 🎮</a>
          <Link to="/nerd-power" className="nerd-btn-secondary text-sm">Testa il tuo Nerd Power ⚡</Link>
        </motion.div>

        <motion.div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-nerd-blue to-transparent mx-auto mt-10"
          initial={{ scaleX: 0 }}
          animate={loaded ? { scaleX: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
        />
      </div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 1 } : {}}
        transition={{ delay: 1.5 }}
      >
        <motion.div className="w-[1px] h-6 bg-gradient-to-b from-nerd-purple to-transparent"
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>

      {/* Corner brackets */}
      <div className="absolute top-3 left-3 w-6 h-6 border-l border-t border-nerd-purple/25 pointer-events-none" />
      <div className="absolute top-3 right-3 w-6 h-6 border-r border-t border-nerd-purple/25 pointer-events-none" />
      <div className="absolute bottom-3 left-3 w-6 h-6 border-l border-b border-nerd-purple/25 pointer-events-none" />
      <div className="absolute bottom-3 right-3 w-6 h-6 border-r border-b border-nerd-purple/25 pointer-events-none" />
    </section>
  )
}
